import { parseDividendStockPriceCsv } from '@apis/dividendStockPriceCsv';
import {
  INSTRUCTIONS_CHART5_SETTING,
  INSTRUCTIONS_CHART_OPTION,
} from '@constants/instructionsChartBundle';
import type { DisclosureNewsApiRow } from '@app-types/instructionsApi';
import type {
  InstructionsChart5InfoData,
  InstructionsChartBundleEventData,
  InstructionsChartBundlePayload,
} from '@app-types/instructions';
import { getDisclosureJstDate } from './instructionsMappers';

export interface InstructionsOneYearRange {
  /** Inclusive Japan calendar date. */
  fromDate: string;
  /** Inclusive Japan calendar date. */
  toDate: string;
  /** Chart5 wire date (`YYYYMMDD`). */
  chart5From: string;
  /** Chart5 wire date (`YYYYMMDD`). */
  chart5To: string;
}

const TOKYO_CALENDAR_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const pad2 = (value: number): string => String(value).padStart(2, '0');

const formatCalendarDate = (year: number, month: number, day: number): string =>
  `${year}-${pad2(month)}-${pad2(day)}`;

const getTokyoCalendarParts = (date: Date): {
  year: number;
  month: number;
  day: number;
} => {
  const parts = TOKYO_CALENDAR_DATE_FORMATTER.formatToParts(date).reduce<
    Record<string, string>
  >((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value;
    return result;
  }, {});

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
  };
};

const getDaysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/** Derives one stable inclusive year from the current Japan calendar date. */
export const getInstructionsOneYearRange = (
  now: Date = new Date(),
): InstructionsOneYearRange => {
  const to = getTokyoCalendarParts(now);
  const fromYear = to.year - 1;
  const fromDay = Math.min(to.day, getDaysInMonth(fromYear, to.month));
  const fromDate = formatCalendarDate(fromYear, to.month, fromDay);
  const toDate = formatCalendarDate(to.year, to.month, to.day);

  return {
    fromDate,
    toDate,
    chart5From: fromDate.replace(/-/g, ''),
    chart5To: toDate.replace(/-/g, ''),
  };
};

/**
 * Keeps the API's HTS rows intact and adds only the leading newline expected
 * by the supplied bundle. A response without a valid price row is no data.
 */
export const normalizeInstructionsChart5Csv = (
  csv: string | null | undefined,
): string | null => {
  if (typeof csv !== 'string' || parseDividendStockPriceCsv(csv).length === 0) {
    return null;
  }

  return csv.startsWith('\n') || csv.startsWith('\r\n') ? csv : `\n${csv}`;
};

/** Stable bundle-local key; it is not sent to either backend API. */
export const buildInstructionsChartInstrumentKey = (
  ticker: string,
  mkt: string,
): string => `${ticker.trim()}.${mkt.trim().toUpperCase()}`;

export const buildInstructionsChart5InfoData = (
  instrumentKey: string,
): InstructionsChart5InfoData => ({
  [instrumentKey]: {
    timeZone: 'Asia/Tokyo',
  },
  info: {
    [instrumentKey]: {
      dp: 2,
      timeZone: 'Asia/Tokyo',
      timezone: 'Asia/Tokyo',
    },
  },
});

/** The current artifact performs decodeURIComponent followed by JSON.parse. */
export const serializeInstructionsChart5InfoData = (
  infoData: InstructionsChart5InfoData,
): string => encodeURIComponent(JSON.stringify(infoData));

/** Escapes every character that can change the bundle's tooltip HTML. */
export const escapeInstructionsChartHeadline = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Maps backend order into same-day red `T` event groups for Chart5. */
export const mapDisclosureNewsToChart5Events = (
  rows: readonly DisclosureNewsApiRow[] | null | undefined,
): InstructionsChartBundleEventData => {
  const eventData: InstructionsChartBundleEventData = {};

  for (const row of rows ?? []) {
    const date = getDisclosureJstDate(row.createdAt);
    if (!date) continue;

    const dateKey = date.replace(/-/g, '');
    const event = {
      type: 'T' as const,
      headline: escapeInstructionsChartHeadline(row.headline?.trim() ?? ''),
    };

    if (eventData[dateKey]) {
      eventData[dateKey].push(event);
    } else {
      eventData[dateKey] = [event];
    }
  }

  return eventData;
};

export interface BuildInstructionsChartBundlePayloadInput {
  ticker: string;
  mkt: string;
  priceCsv: string | null | undefined;
  disclosureRows: readonly DisclosureNewsApiRow[] | null | undefined;
}

/** Builds the complete credential-free message sent after the iframe is ready. */
export const buildInstructionsChartBundlePayload = ({
  ticker,
  mkt,
  priceCsv,
  disclosureRows,
}: BuildInstructionsChartBundlePayloadInput): InstructionsChartBundlePayload | null => {
  const chartData = normalizeInstructionsChart5Csv(priceCsv);
  if (!ticker.trim() || !mkt.trim() || !chartData) return null;

  const instrumentKey = buildInstructionsChartInstrumentKey(ticker, mkt);
  const infoData = buildInstructionsChart5InfoData(instrumentKey);

  return {
    source: 'labci-chart5',
    theme: 'light',
    ric: instrumentKey,
    chartData,
    periodType: 'hts',
    chartType: 'line',
    period: '1Y',
    interval: 'd',
    infoData: serializeInstructionsChart5InfoData(infoData),
    eventData: mapDisclosureNewsToChart5Events(disclosureRows),
    chart5Setting: { ...INSTRUCTIONS_CHART5_SETTING },
    chartOption: { ...INSTRUCTIONS_CHART_OPTION },
    showCompare: false,
    showCrosshair: false,
    showMainChart: true,
    showTracker: true,
    showScrollView: false,
    showTA: false,
    showDrawTool: false,
    // Instructions chart is read-only: keep marker hover/tap available but do
    // not allow the price series to pan by dragging.
    drag2Slide: false,
    lang: 'ja',
  };
};
